/**
 * Metadados VISUAIS dos tipos de nó das automações (issues #137 e #148).
 *
 * O que o nó é e quais campos tem vem do backend (`/automations/catalog`,
 * registry em api/automations/nodes.py). Aqui fica só o que é de tela: ícone,
 * cor e o resumo de uma linha mostrado no card do canvas. Tipo novo sem
 * entrada aqui cai no visual padrão do seu `kind` — o editor segue funcionando.
 */

import { AGENDA_NODE_META, summarizeAgenda } from './nodeMetaAgenda.js'
import { FINANCEIRO_NODE_META, summarizeFinanceiro } from './nodeMetaFinanceiro.js'
import { MARKETING_NODE_META, summarizeMarketing } from './nodeMetaMarketing.js'
import { COMERCIAL_COMUNICACAO_NODE_META, summarizeComercialComunicacao } from './nodeMetaComercialComunicacao.js'
import { COMERCIAL_LEADS_NODE_META, summarizeComercialLeads } from './nodeMetaComercialLeads.js'
import { COMERCIAL_NEGOCIOS_NODE_META, summarizeComercialNegocios } from './nodeMetaComercialNegocios.js'

const KIND_META = {
  trigger: { label: 'Gatilho', icon: 'zap', tone: 'amber' },
  condition: { label: 'Condição', icon: 'filter', tone: 'violet' },
  action: { label: 'Ação', icon: 'spark', tone: 'blue' },
}

const NODE_META = {
  'trigger.lead_created': { icon: 'leads' },
  'trigger.deal_stage_changed': { icon: 'chart' },
  'trigger.whatsapp_message_received': { icon: 'whatsapp' },
  'flow.condition': { icon: 'filter' },
  'flow.wait': { icon: 'clock', tone: 'slate' },
  'action.send_whatsapp': { icon: 'whatsapp', tone: 'green' },
  'action.create_activity': { icon: 'check' },
  'action.move_deal_stage': { icon: 'arrow_right' },
  'trigger.deal_closed': { icon: 'check' },
  'trigger.activity_overdue': { icon: 'alert' },
  'trigger.schedule': { icon: 'calendar' },
  'trigger.webhook': { icon: 'link' },
  'flow.switch': { icon: 'columns' },
  'flow.wait_business_hours': { icon: 'briefcase', tone: 'slate' },
  'flow.wait_reply': { icon: 'inbox' },
  'action.create_deal': { icon: 'dollar_circle' },
  'action.assign_owner': { icon: 'user' },
  'action.update_tags': { icon: 'tag' },
  'action.http_request': { icon: 'external', tone: 'slate' },
  // #148
  'trigger.lead_tag_added': { icon: 'tag' },
  'trigger.lead_tag_removed': { icon: 'tag' },
  'trigger.lead_owner_changed': { icon: 'user' },
  'trigger.deal_created': { icon: 'dollar_circle' },
  'trigger.deal_stalled': { icon: 'timer' },
  'trigger.activity_created': { icon: 'check' },
  'trigger.activity_completed': { icon: 'ticks' },
  'trigger.receivable_overdue': { icon: 'wallet' },
  'trigger.task_stage_changed': { icon: 'columns' },
  'trigger.task_completed': { icon: 'ticks' },
  'trigger.whatsapp_meta_message_received': { icon: 'whatsapp' },
  'flow.split': { icon: 'percent' },
  'flow.wait_until': { icon: 'calendar', tone: 'slate' },
  'flow.end': { icon: 'close', tone: 'slate' },
  'action.update_lead_field': { icon: 'edit' },
  'action.update_deal_field': { icon: 'edit' },
  'action.close_deal': { icon: 'star' },
  'action.add_note': { icon: 'page' },
  'action.notify_user': { icon: 'bell' },
  'action.complete_activity': { icon: 'ticks' },
  'action.create_project_task': { icon: 'folder' },
  'action.send_whatsapp_media': { icon: 'clip', tone: 'green' },
  'action.conversation': { icon: 'attend' },
  'action.create_event': { icon: 'calendar' },
}

// ---------------------------------------------------------------------------
// Atendimento (issue #154): ícones e resumos dos nós de conversa/mensagem.
// Bloco à parte para não misturar com os outros módulos (metaFor e o `default`
// de summarize consultam estes).
// ---------------------------------------------------------------------------
const ATENDIMENTO_NODE_META = {
  'trigger.conversation_created': { icon: 'attend' },
  'trigger.conversation_assigned': { icon: 'user' },
  'trigger.conversation_unassigned': { icon: 'user' },
  'trigger.conversation_status_changed': { icon: 'inbox' },
  'trigger.conversation_favorited': { icon: 'star' },
  'trigger.conversation_marked_unread': { icon: 'eye' },
  'trigger.conversation_linked': { icon: 'link' },
  'trigger.conversation_participant_changed': { icon: 'users' },
  'trigger.conversation_message_sent': { icon: 'send' },
  'trigger.conversation_message_from_device': { icon: 'phone' },
  'trigger.conversation_message_attachment': { icon: 'clip' },
  'trigger.conversation_message_status': { icon: 'ticks' },
  'trigger.conversation_first_message': { icon: 'spark' },
  'trigger.conversation_no_operator_reply': { icon: 'timer' },
  'trigger.conversation_no_contact_reply': { icon: 'timer' },
  'trigger.conversation_unassigned_for': { icon: 'timer' },
  'trigger.conversation_idle': { icon: 'clock' },
  'trigger.conversation_unread_for': { icon: 'bell' },
  'flow.business_hours': { icon: 'briefcase', tone: 'slate' },
  'action.conversation_assign': { icon: 'user' },
  'action.conversation_state': { icon: 'inbox' },
  'action.conversation_link': { icon: 'link' },
  'action.conversation_participant': { icon: 'users' },
  'action.conversation_create': { icon: 'attend' },
  'action.conversation_send_message': { icon: 'whatsapp', tone: 'green' },
  'action.conversation_resend_failed': { icon: 'refresh', tone: 'green' },
  'action.conversation_system_note': { icon: 'page' },
  'action.conversation_create_deal': { icon: 'dollar_circle' },
  'action.conversation_create_activity': { icon: 'check' },
  'action.conversation_notify': { icon: 'bell' },
  'action.conversation_summary': { icon: 'sparkle' },
}

const ATD_OPCOES = {
  origem: { manual: 'criada pelo operador', ponte: 'contato chamou', automacao: 'criada por automação' },
  evento: { encerrada: 'encerrada', reaberta: 'reaberta', arquivada: 'arquivada' },
  vinculo: { negocio: 'negócio', empresa: 'empresa', contato: 'contato' },
  anexo: { imagem: 'imagem', audio: 'áudio', video: 'vídeo', documento: 'documento' },
  recibo: { falhou: 'falhou', entregue: 'entregue', lido: 'lida' },
  operacao: {
    close: 'Encerra a conversa', reopen: 'Reabre a conversa', archive: 'Arquiva a conversa',
    favorite: 'Favorita a conversa', unfavorite: 'Desfavorita a conversa',
    mark_read: 'Marca como lida', mark_unread: 'Marca como não lida',
  },
}

/** Resumo de uma linha dos nós do Atendimento; '' quando a chave não é deles. */
function atendimentoSummary(key, config = {}, type = null, names = {}) {
  const nome = (id) => names[id] || null
  const trunc = (s, n = 40) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '')
  switch (key) {
    case 'trigger.conversation_created':
      return config.origem ? `Conversa ${ATD_OPCOES.origem[config.origem] || config.origem}` : 'Qualquer conversa nova'
    case 'trigger.conversation_assigned':
      if (config.to_user_id) return `Atribuída a ${nome(config.to_user_id) || 'pessoa selecionada'}`
      return config.from_user_id ? `Transferida de ${nome(config.from_user_id) || 'pessoa selecionada'}` : 'Qualquer atribuição ou transferência'
    case 'trigger.conversation_unassigned':
      return config.from_user_id ? `Ficou sem ${nome(config.from_user_id) || 'pessoa selecionada'}` : 'Qualquer conversa desatribuída'
    case 'trigger.conversation_status_changed':
      return config.evento ? `Conversa ${ATD_OPCOES.evento[config.evento] || config.evento}` : 'Encerrada, reaberta ou arquivada'
    case 'trigger.conversation_favorited':
      return 'Conversa favoritada'
    case 'trigger.conversation_marked_unread':
      return 'Conversa marcada como não lida'
    case 'trigger.conversation_linked':
      return config.vinculo ? `Vinculada a ${ATD_OPCOES.vinculo[config.vinculo] || config.vinculo}` : 'Qualquer vínculo'
    case 'trigger.conversation_participant_changed':
      return `${config.acao === 'removido' ? 'Removido' : config.acao === 'adicionado' ? 'Adicionado' : 'Adicionado ou removido'}${config.user_id ? `: ${nome(config.user_id) || 'pessoa selecionada'}` : ''}`
    case 'trigger.conversation_message_sent': {
      const quem = { automacao: 'automação', qualquer: 'operador ou automação' }[config.origem] || 'operador'
      return config.contains ? `Enviada por ${quem} contendo “${trunc(config.contains, 24)}”` : `Enviada por ${quem}`
    }
    case 'trigger.conversation_message_from_device':
      return config.contains ? `Outro dispositivo contendo “${trunc(config.contains, 24)}”` : 'Enviada de outro dispositivo'
    case 'trigger.conversation_message_attachment':
      return config.tipo_anexo ? `Anexo: ${ATD_OPCOES.anexo[config.tipo_anexo] || config.tipo_anexo}` : 'Qualquer anexo'
    case 'trigger.conversation_message_status':
      return config.message_status ? `Mensagem ${ATD_OPCOES.recibo[config.message_status] || config.message_status}` : 'Falha ou recibo de mensagem'
    case 'trigger.conversation_first_message':
      return config.remetente ? `Primeira mensagem do ${config.remetente}` : 'Primeira mensagem da conversa'
    case 'trigger.conversation_no_operator_reply':
      return config.minutes ? `Sem resposta do operador há ${config.minutes} min` : 'Informe os minutos'
    case 'trigger.conversation_no_contact_reply':
      return config.minutes ? `Sem resposta do contato há ${config.minutes} min` : 'Informe os minutos'
    case 'trigger.conversation_unassigned_for':
      return config.minutes ? `Sem responsável há ${config.minutes} min` : 'Informe os minutos'
    case 'trigger.conversation_idle':
      return config.days ? `Parada há ${config.days} dia(s)` : 'Informe os dias'
    case 'trigger.conversation_unread_for':
      return config.minutes ? `Não lida há ${config.minutes} min` : 'Informe os minutos'
    case 'flow.business_hours':
      return config.days?.length && config.start && config.end
        ? `Atendimento ${config.start}–${config.end}` : 'Configure o horário de atendimento'
    case 'action.conversation_assign':
      if (config.mode === 'unassign') return 'Remove o responsável'
      if (config.mode === 'round_robin') return `Rodízio entre ${(config.user_ids || []).length} pessoa(s)`
      if (config.mode === 'load') return `Menor carga entre ${(config.user_ids || []).length} pessoa(s)`
      return config.user_id ? `Para ${nome(config.user_id) || 'pessoa selecionada'}` : 'Escolha o responsável'
    case 'action.conversation_state':
      return ATD_OPCOES.operacao[config.operation] || 'Escolha a operação'
    case 'action.conversation_link':
      return { deal: 'Vincula ao negócio', company: 'Vincula à empresa', contact: 'Vincula ao lead' }[config.target] || 'Escolha o vínculo'
    case 'action.conversation_participant':
      if (!config.user_id) return 'Escolha a pessoa'
      return `${config.operation === 'remove' ? 'Remove' : 'Adiciona'} ${nome(config.user_id) || 'pessoa selecionada'}`
    case 'action.conversation_create':
      return config.message ? `Abre a conversa e envia “${trunc(config.message, 30)}”` : 'Abre a conversa no Atendimento'
    case 'action.conversation_send_message': {
      const tipo = { image: 'Imagem', audio: 'Áudio', video: 'Vídeo', document: 'Documento' }[config.kind]
      if (tipo) return `${tipo}${config.file_name ? `: ${trunc(config.file_name, 30)}` : ''}`
      return config.message ? trunc(config.message, 60) : 'Escreva a mensagem'
    }
    case 'action.conversation_resend_failed':
      return 'Reenvia a última mensagem com falha'
    case 'action.conversation_system_note':
      return config.text ? trunc(config.text, 60) : 'Escreva o texto'
    case 'action.conversation_create_deal':
      return config.stage_id ? `Em ${nome(config.stage_id) || 'etapa selecionada'}` : 'Escolha funil e etapa'
    case 'action.conversation_create_activity':
      return config.titulo ? trunc(config.titulo, 60) : 'Defina o título'
    case 'action.conversation_notify':
      return config.title ? `${config.user_id ? `${nome(config.user_id) || 'Pessoa'}: ` : ''}${trunc(config.title, 44)}` : 'Defina o título'
    case 'action.conversation_summary':
      return `Últimas ${config.last_n || 20} mensagens${config.save_note === 'no' ? '' : ' na timeline'}`
    default:
      return ''
  }
}

// #159 — nós da Agenda (ícones em nodeMetaAgenda.js)
Object.assign(NODE_META, AGENDA_NODE_META)
// #169 — nós do Financeiro (ícones em nodeMetaFinanceiro.js)
Object.assign(NODE_META, FINANCEIRO_NODE_META)
// #150 — nós de Marketing (ícones em nodeMetaMarketing.js)
Object.assign(NODE_META, MARKETING_NODE_META)
// #157 — nós do Comercial (ícones e resumos em nodeMetaComercialComunicacao.js)
Object.assign(NODE_META, COMERCIAL_COMUNICACAO_NODE_META)
// #155 — nós do Comercial (ícones em nodeMetaComercialLeads.js)
Object.assign(NODE_META, COMERCIAL_LEADS_NODE_META)
// #156 — nós do Comercial (ícones em nodeMetaComercialNegocios.js)
Object.assign(NODE_META, COMERCIAL_NEGOCIOS_NODE_META)

/**
 * Saídas de um nó com vários caminhos: [{ id, label }] ou null (saída única).
 * Fixas (condição, esperar resposta) ou vindas da config (switch: um caminho
 * por valor + "Outros").
 */
export function nodeHandles(type, config = {}) {
  if (!type) return null
  const labels = type.handle_labels || {}
  if (type.handles_from) {
    const casos = (config[type.handles_from] || [])
      .filter((c) => c?.id)
      .map((c) => ({ id: c.id, label: c.value?.trim() || '…' }))
    return [...casos, { id: 'other', label: labels.other || 'Outros' }]
  }
  if (type.handles?.length) return type.handles.map((h) => ({ id: h, label: labels[h] || h }))
  return null
}

const vazio = (v) => v == null || v === '' || (Array.isArray(v) && !v.length)

/** Campo escondido pela config atual (regra `hidden_when` do catálogo). */
export function isFieldHidden(field, config = {}) {
  const rule = field.hidden_when
  if (!rule) return false
  return Object.entries(rule).some(([name, values]) => values.includes(config[name] ?? null)
    || (config[name] === '' && values.includes('')))
}

/**
 * Checagem rápida no front para destacar o passo incompleto no canvas. A
 * validação completa é do backend (lista de erros ao salvar/ativar).
 */
export function hasConfigProblem(type, config = {}) {
  if (!type) return false
  return (type.fields || []).some((f) => {
    if (isFieldHidden(f, config)) return false
    if (f.type === 'rules') {
      const rules = config.rules?.length ? config.rules : (config.field ? [config] : [])
      return !rules.length || rules.some((r) => !r.field || !r.operator)
    }
    if (f.type === 'cases') return !(config.cases || []).length || config.cases.some((c) => !String(c.value || '').trim())
    return f.required && vazio(config[f.name])
  })
}

const DIAS = ['', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom']
const diasTexto = (days = []) => {
  const set = [...new Set(days.map(Number))].sort()
  if (set.join() === '1,2,3,4,5') return 'seg–sex'
  if (set.length === 7) return 'todos os dias'
  return set.map((d) => DIAS[d]).join(', ')
}

// Grupos da paleta lateral, na ordem em que aparecem.
export const PALETTE_GROUPS = [
  { kind: 'trigger', label: 'Gatilhos' },
  { kind: 'condition', label: 'Lógica' },
  { kind: 'action', label: 'Ações' },
]

// Ordem dos módulos na paleta; módulo fora da lista vai para o fim, por nome.
const MODULE_ORDER = ['Comercial', 'Atendimento', 'Agenda', 'Financeiro', 'Marketing', 'Projetos', 'Geral']

const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/**
 * Itens de um tipo (gatilho/lógica/ação) da paleta, filtrados pela busca
 * (nome, descrição ou módulo; sem acento nem caixa) e agrupados por módulo (#304).
 * Retorna [{ module, items }] só com grupos não vazios.
 */
export function paletteSections(catalog, kind, query = '') {
  const termos = semAcento(query).split(/\s+/).filter(Boolean)
  const grupos = new Map()
  for (const t of Object.values(catalog || {})) {
    if (t.kind !== kind) continue
    const modulo = t.module || 'Geral'
    const alvo = semAcento(`${t.label} ${t.description} ${modulo}`)
    if (!termos.every((x) => alvo.includes(x))) continue
    if (!grupos.has(modulo)) grupos.set(modulo, [])
    grupos.get(modulo).push(t)
  }
  const pos = (m) => (MODULE_ORDER.includes(m) ? MODULE_ORDER.indexOf(m) : MODULE_ORDER.length)
  return [...grupos.entries()]
    .sort(([a], [b]) => pos(a) - pos(b) || a.localeCompare(b))
    .map(([module, items]) => ({ module, items }))
}

export function metaFor(type) {
  const kind = KIND_META[type?.kind] || KIND_META.action
  const own = NODE_META[type?.key] || ATENDIMENTO_NODE_META[type?.key] || {}
  return { icon: own.icon || kind.icon, tone: own.tone || kind.tone, kindLabel: kind.label }
}

const UNIDADES = { minutes: 'minuto(s)', hours: 'hora(s)', days: 'dia(s)' }

const trunc = (s, n = 60) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '')

/**
 * Resumo de uma linha da configuração do nó (card do canvas e log).
 * `names` resolve ids em nomes (etapas, funis) quando a tela já carregou.
 */
export function summarize(key, config = {}, type = null, names = {}) {
  const nome = (id) => names[id] || null
  const agenda = summarizeAgenda(key, config, names) // #159
  if (agenda !== undefined) return agenda
  const financeiro = summarizeFinanceiro(key, config) // #169
  if (financeiro !== undefined) return financeiro
  const marketing = summarizeMarketing(key, config) // #150
  if (marketing !== undefined) return marketing
  const comercialCom = summarizeComercialComunicacao(key, config, type) // #157
  if (comercialCom !== undefined) return comercialCom
  const comercialLeads = summarizeComercialLeads(key, config, names) // #155
  if (comercialLeads !== undefined) return comercialLeads
  const comercial = summarizeComercialNegocios(key, config, names) // #156
  if (comercial !== undefined) return comercial
  switch (key) {
    case 'trigger.lead_created':
      return 'Qualquer lead novo'
    case 'trigger.deal_stage_changed':
      if (config.to_stage_id) return `Entrou em ${nome(config.to_stage_id) || 'etapa selecionada'}`
      if (config.pipeline_id) return `Qualquer etapa de ${nome(config.pipeline_id) || 'funil selecionado'}`
      return 'Qualquer mudança de etapa'
    case 'trigger.whatsapp_message_received':
      return config.contains ? `Mensagem contém “${trunc(config.contains, 30)}”` : 'Qualquer mensagem recebida'
    case 'flow.condition': {
      // Condições antigas (#137) têm uma regra direto na config.
      const rules = config.rules?.length ? config.rules
        : (config.field ? [{ field: config.field, operator: config.operator, value: config.value }] : [])
      if (!rules.length) return 'Configure as regras'
      const spec = type?.fields?.find((f) => f.name === 'rules') || {}
      const r = rules[0]
      const field = spec.fields?.find((o) => o.value === r.field)
      const op = spec.operators?.find((o) => o.value === r.operator)
      const valor = ['is_empty', 'is_not_empty'].includes(r.operator) ? '' : ` “${trunc(r.value || '', 20)}”`
      const primeira = `${field?.label || r.field || '?'} ${op?.label || ''}${valor}`
      if (rules.length === 1) return primeira
      return `${primeira} ${config.combinator === 'or' ? 'OU' : 'E'} mais ${rules.length - 1}`
    }
    case 'flow.switch': {
      const field = type?.fields?.find((f) => f.name === 'field')?.options?.find((o) => o.value === config.field)
      const n = (config.cases || []).length
      return config.field ? `${field?.label || config.field} · ${n} caminho(s)` : 'Escolha o campo'
    }
    case 'flow.wait_business_hours':
      return config.days?.length && config.start && config.end
        ? `Expediente ${diasTexto(config.days)} ${config.start}–${config.end}`
        : 'Configure o expediente'
    case 'flow.wait_reply':
      return config.amount ? `Até ${config.amount} ${UNIDADES[config.unit] || ''} pela resposta${config.by === 'conversation' ? ' na conversa' : ''}` : 'Configure o tempo limite'
    case 'trigger.deal_closed': {
      const res = config.status === 'ganho' ? 'ganho' : config.status === 'perdido' ? 'perdido' : 'ganho ou perdido'
      return config.pipeline_id ? `Negócio ${res} em ${nome(config.pipeline_id) || 'funil selecionado'}` : `Negócio ${res}`
    }
    case 'trigger.activity_overdue':
      return config.tipo ? `Atividade do tipo ${config.tipo} vencida` : 'Qualquer atividade vencida'
    case 'trigger.schedule':
      return config.days?.length && config.time ? `${diasTexto(config.days)} às ${config.time}` : 'Configure dias e horário'
    case 'trigger.webhook':
      return 'Chamada externa na URL da automação'
    case 'action.create_deal':
      return config.stage_id ? `Em ${nome(config.stage_id) || 'etapa selecionada'}` : 'Escolha funil e etapa'
    case 'action.assign_owner':
      if (config.mode === 'round_robin') return `Rodízio entre ${(config.user_ids || []).length} pessoa(s)`
      return config.user_id ? `Para ${nome(config.user_id) || 'pessoa selecionada'}` : 'Escolha o responsável'
    case 'action.update_tags':
      return config.tags ? `${config.operation === 'remove' ? 'Remove' : 'Adiciona'}: ${trunc(config.tags, 40)}` : 'Informe as tags'
    case 'action.http_request':
      return config.url ? `${config.method || 'POST'} ${trunc(config.url, 44)}` : 'Informe a URL'
    case 'flow.wait':
      return config.amount ? `Espera ${config.amount} ${UNIDADES[config.unit] || ''}` : 'Configure o tempo'
    case 'action.send_whatsapp':
      return config.message ? trunc(config.message) : 'Escreva a mensagem'
    case 'action.create_activity':
      return config.titulo ? trunc(config.titulo) : 'Defina o título'
    case 'action.move_deal_stage':
      return config.stage_id ? `Para ${nome(config.stage_id) || 'etapa selecionada'}` : 'Escolha a etapa'
    // ---- #148
    case 'trigger.lead_tag_added':
      return config.tag ? `Tag adicionada: ${trunc(config.tag, 40)}` : 'Qualquer tag adicionada'
    case 'trigger.lead_tag_removed':
      return config.tag ? `Tag removida: ${trunc(config.tag, 40)}` : 'Qualquer tag removida'
    case 'trigger.lead_owner_changed':
      return config.user_id ? `Passou para ${nome(config.user_id) || 'pessoa selecionada'}` : 'Qualquer mudança de responsável'
    case 'trigger.deal_created':
      return config.pipeline_id ? `Negócio novo em ${nome(config.pipeline_id) || 'funil selecionado'}` : 'Qualquer negócio novo'
    case 'trigger.deal_stalled': {
      if (!config.days) return 'Informe os dias parado'
      const onde = config.stage_id ? ` em ${nome(config.stage_id) || 'etapa selecionada'}`
        : config.pipeline_id ? ` em ${nome(config.pipeline_id) || 'funil selecionado'}` : ''
      return `Parado há ${config.days} dia(s)${onde}`
    }
    case 'trigger.activity_created':
      return config.tipo ? `Atividade do tipo ${config.tipo} criada` : 'Qualquer atividade criada'
    case 'trigger.activity_completed':
      return config.tipo ? `Atividade do tipo ${config.tipo} concluída` : 'Qualquer atividade concluída'
    case 'trigger.receivable_overdue':
      return config.min_days > 1 ? `Conta com ${config.min_days}+ dias de atraso` : 'Conta a receber vencida'
    case 'trigger.task_stage_changed':
      if (config.stage_nome) return `Entrou em “${trunc(config.stage_nome, 30)}”`
      return config.project ? `Tarefa de ${trunc(config.project, 30)} mudou de coluna` : 'Qualquer mudança de coluna'
    case 'trigger.task_completed':
      return config.project ? `Tarefa de ${trunc(config.project, 30)} concluída` : 'Qualquer tarefa concluída'
    case 'trigger.whatsapp_meta_message_received':
      return config.contains ? `Mensagem contém “${trunc(config.contains, 30)}”` : 'Qualquer mensagem (API oficial)'
    case 'flow.split': {
      const a = Number(config.percent_a)
      return Number.isFinite(a) && config.percent_a !== '' && config.percent_a != null
        ? `A ${a}% · B ${100 - a}%` : 'Defina a porcentagem'
    }
    case 'flow.wait_until': {
      const ajuste = Number(config.offset_minutes) || 0
      const sufixo = ajuste ? ` (${ajuste > 0 ? '+' : ''}${ajuste} min)` : ''
      if (config.mode === 'activity_deadline') return `Até o prazo da atividade${sufixo}`
      return config.at ? `Até ${trunc(config.at, 30)}${sufixo}` : 'Defina a data e hora'
    }
    case 'flow.end':
      return config.reason ? trunc(config.reason) : 'Encerra a execução'
    case 'action.update_lead_field':
    case 'action.update_deal_field': {
      const campos = type?.fields?.find((f) => f.name === 'field')?.options || []
      const label = campos.find((o) => o.value === config.field)?.label || config.field
      return config.field ? `${label} = ${trunc(config.value || '…', 30)}` : 'Escolha o campo'
    }
    case 'action.close_deal':
      if (!config.result) return 'Escolha o resultado'
      return config.result === 'ganho' ? 'Marca como ganho'
        : `Marca como perdido${config.loss_reason ? `: ${trunc(config.loss_reason, 30)}` : ''}`
    case 'action.add_note':
      return config.text ? trunc(config.text) : 'Escreva a nota'
    case 'action.notify_user':
      return config.title ? `${config.user_id ? `${nome(config.user_id) || 'Pessoa'}: ` : ''}${trunc(config.title, 44)}` : 'Defina o título'
    case 'action.complete_activity':
      return config.scope === 'open' ? 'Todas as pendentes do lead/negócio' : 'Atividade do fluxo'
    case 'action.create_project_task':
      return config.titulo ? `${config.project ? `${trunc(config.project, 20)}: ` : ''}${trunc(config.titulo, 40)}` : 'Defina o título'
    case 'action.send_whatsapp_media':
      if (!config.media_type) return 'Escolha o tipo e o arquivo'
      return `${{ document: 'Documento', audio: 'Áudio', video: 'Vídeo' }[config.media_type] || 'Imagem'}${config.file_name ? `: ${trunc(config.file_name, 30)}` : ''}`
    case 'action.conversation':
      if (config.operation === 'close') return 'Encerra a conversa'
      if (config.operation === 'assign') return config.user_id ? `Atribui a ${nome(config.user_id) || 'pessoa selecionada'}` : 'Atribui ao responsável'
      return 'Escolha a operação'
    case 'action.create_event':
      if (!config.titulo) return 'Defina o título'
      return `${trunc(config.titulo, 34)}${config.hora ? ` · ${config.hora}` : ''}`
    default:
      return atendimentoSummary(key, config, type, names) || type?.description || ''
  }
}
